
import React from 'react';
import { normalizeCountryCode } from '@/utils/countries';

export const useRoyaltyReportsData = (royaltyReports: any[]) => {
  // Extract unique years, statuses, and countries for filters
  const years = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const uniqueYears = new Set(royaltyReports.map(report => report.year));
    return Array.from(uniqueYears).sort((a, b) => b - a); // Sort descending
  }, [royaltyReports]);

  const statuses = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const uniqueStatuses = new Set(royaltyReports.map(report => report.status));
    return Array.from(uniqueStatuses);
  }, [royaltyReports]);

  const countries = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const uniqueCountries = new Set(royaltyReports.map(report => normalizeCountryCode(report.country || 'US')));
    return Array.from(uniqueCountries).sort();
  }, [royaltyReports]);

  // Group reports by quarter, year, and author
  const groupedReports = React.useMemo(() => {
    if (!royaltyReports) return [];
    
    const groups = new Map();
    
    royaltyReports.forEach(report => {
      const key = `${report.authorId}-${report.year}-${report.quarter}`;
      if (!groups.has(key)) {
        groups.set(key, {
          authorId: report.authorId,
          year: report.year,
          quarter: report.quarter,
          reports: [],
          totalSales: 0,
          totalAmount: 0,
          status: report.status,
          countries: new Set()
        });
      }
      
      const group = groups.get(key);
      group.reports.push(report);
      group.totalSales += report.unitsSold || report.salesCount || 0;
      group.totalAmount += report.royaltyAmount || report.amount || 0;
      group.countries.add(normalizeCountryCode(report.country || 'US'));
    });
    
    return Array.from(groups.values());
  }, [royaltyReports]);

  return {
    years,
    statuses,
    countries,
    groupedReports
  };
};
